import React, { useEffect, useState } from 'react';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/ethers';
import { Spinner } from '../common/Loader';

function pctToBps(pctStr) {
  const n = Number(pctStr);
  if (!isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}
function bpsToPctStr(bps) {
  const n = Number(bps) / 100;
  return String(parseFloat(n.toFixed(2)));
}

export default function LevelConfigPanel() {
  const { read, write } = useContracts();
  const notify = useNotify();
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(-1);

  const load = () => {
    read.storage.getAllLevelConfig().then((cfg) => {
      const arr = cfg.bps.map((b, i) => ({
        pct: bpsToPctStr(b),
        directs: cfg.directsReq[i].toString(),
      }));
      setRows(arr);
      setDrafts(arr);
    }).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const update = (i, field, val) => {
    const copy = drafts.slice();
    copy[i] = { ...copy[i], [field]: val };
    setDrafts(copy);
  };

  async function save(i) {
    if (!write) return notify.error('Wallet not connected.');
    const d = drafts[i];
    const bps = pctToBps(d.pct);
    if (bps < 0 || bps > 10000) return notify.error('BPS must be 0..10000 (0..100%).');
    setBusy(i);
    try {
      const tx = await write.storage.setLevelConfig(i, bps, d.directs || 0);
      notify.info('Saving level...');
      await tx.wait();
      notify.success(`Level ${i + 1} updated.`);
      load();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setBusy(-1);
    }
  }

  if (!drafts.length) return <div className="admin-panel"><Spinner /></div>;

  const totalPct = drafts.reduce((a, d) => a + (Number(d.pct) || 0), 0);

  return (
    <div className="admin-panel">
      <h3>Level Rewards Config</h3>
      <p className="panel-hint">
        Configure the 12-level referral rewards. Each level's % is paid on every purchase from a user
        at that depth in the sponsor tree.
      </p>

      <div className="bps-sum ok" style={{ borderColor: 'rgba(0, 229, 160, 0.3)' }}>
        <span>Total Level Payout</span>
        <strong>{totalPct.toFixed(2)}% (of every purchase)</strong>
      </div>

      {drafts.map((d, i) => {
        const changed = JSON.stringify(d) !== JSON.stringify(rows[i]);
        return (
          <div key={i} className="tier-editor" style={{ marginTop: 12 }}>
            <div className="tier-editor-header">
              <div className="name">Level {i + 1}</div>
              {i === 0 && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Directs required: usually 0 for L1
                </span>
              )}
            </div>
            <div className="tier-fields">
              <div>
                <label className="small-label">Reward %</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="small-input"
                    value={d.pct}
                    onChange={(e) => update(i, 'pct', e.target.value.replace(/[^0-9.]/g, ''))}
                    style={{ paddingRight: 22 }}
                  />
                  <span style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--text-muted)', fontSize: 11, pointerEvents: 'none',
                  }}>%</span>
                </div>
              </div>
              <div>
                <label className="small-label">Directs Required</label>
                <input
                  className="small-input"
                  value={d.directs}
                  onChange={(e) => update(i, 'directs', e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <button
                className="btn btn-primary btn-sm"
                disabled={!changed || busy === i}
                onClick={() => save(i)}
              >
                {busy === i ? <Spinner /> : 'Save'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
